import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import translationsData from '../../data/translations.json';
import { handleLocalApiCall } from '../../api/localGameBackend';

const GameContext = createContext();

export const GameProvider = ({ children }) => {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('sessionId'));
  const [sessionCode, setSessionCode] = useState(null);
  const [currentCounter, setCurrentCounter] = useState(null);
  const [currentCards, setCurrentCards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError, ] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem('language') || 'EN');
  const [cardLang, setCardLang] = useState(() => localStorage.getItem('cardLanguage') || 'EN');
  const [translations, setTranslations] = useState(translationsData);
  const [isLocalhost, setIsLocalhost] = useState(false);

  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const toastTimeoutRef = useRef(null);
  const fadeOutTimeoutRef = useRef(null);

  const qrScannerModalRef = useRef(null);
  const alertModalRef = useRef(null); 

  const [outcomeCardData, setOutcomeCardData] = useState(() => {
    try {
      const storedOutcome = localStorage.getItem('outcomeCardData');
      return storedOutcome ? JSON.parse(storedOutcome) : null;
    } catch (e) {
      console.error('Failed to parse outcomeCardData from localStorage:', e);
      return null;
    }
  }); 

  const [hypothesisChangedFlag, setHypothesisChangedFlag] = useState(0);

  const [lastScannedCardCode, setLastScannedCardCode] = useState(null);

  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [alertModalState, setAlertModalState] = useState({
    title: '',
    message: '',
    isConfirmation: false,
    onConfirm: null,
    onClose: null,
    confirmButtonText: '',
    cancelButtonText: ''
  });

  const [gameStatus, setGameStatus] = useState(() => {
    const hash = window.location.hash.substring(1);
    const storedOutcome = localStorage.getItem('outcomeCardData');
    if (hash === 'outcome' && storedOutcome) {
      return 'outcome';
    }
    return 'initial';
  }); 

  const [isPollingUpdate, setIsPollingUpdate] = useState(false);
  
  const clearHypothesisLocalStorage = useCallback(() => {
    console.log('Clearing hypothesis data from localStorage (iterating backwards).');
    const keysToProcess = [];
    for (let i = 0; i < localStorage.length; i++) {
      keysToProcess.push(localStorage.key(i));
    }

    for (let i = keysToProcess.length - 1; i >= 0; i--) {
      const key = keysToProcess[i];
      if (key && key.startsWith('hypothesis_')) {
        localStorage.removeItem(key);
        console.log(`Hypothesis key removed: ${key}`);
      }
    }
  }, []);

  const resetGame = useCallback(() => {
    setSessionId(null);
    setSessionCode(null); 
    setCurrentCounter(0);
    setCurrentCards([]);
    setOutcomeCardData(null);
    clearHypothesisLocalStorage(); 
    localStorage.removeItem('sessionId');
    localStorage.removeItem('cardLanguage'); 
    localStorage.removeItem('outcomeCardData'); 
    setGameStatus('initial'); 
    window.location.hash = ''; 
  }, [setSessionId, clearHypothesisLocalStorage, setGameStatus]);

  useEffect(() => {
    setIsLocalhost(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  }, []);

  useEffect(() => {
    if (translations[lang] && translations[lang].page_title) {
      document.title = translations[lang].page_title;
    }
  }, [lang, translations]);

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash === 'outcome') {
      try {
        const storedOutcome = localStorage.getItem('outcomeCardData');
        if (storedOutcome) {
          setOutcomeCardData(JSON.parse(storedOutcome));
          setGameStatus('outcome');
          console.log('GameContext: Restored outcomeCardData from localStorage.');
        } else {
          console.warn('GameContext: Hash is "outcome" but no outcomeCardData found in localStorage. Redirecting to initial.');
          resetGame();
        }
      } catch (e) {
        console.error('GameContext: Error restoring outcomeCardData on mount:', e);
        resetGame();
      }
    }
  }, [lang, translations, resetGame]);

  const showToastMessage = useCallback((message) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    if (fadeOutTimeoutRef.current) {
      clearTimeout(fadeOutTimeoutRef.current); 
    }

    setToastMessage(message);
    setShowToast(true);

    toastTimeoutRef.current = setTimeout(() => {
      setShowToast(false);
      fadeOutTimeoutRef.current = setTimeout(() => {
        setToastMessage('');
      }, 1000);
    }, 5000);
  }, []);

  const makeApiCall = useCallback(async (method, path, body = undefined, suppressLoadingIndicator = false) => {
    if (!suppressLoadingIndicator) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const data = await handleLocalApiCall(method, path, body);
      return data;
    } catch (err) {
      console.error(`Request error ${method} ${path}:`, err);
      const errorMessage = `API Request Error: ${err.message}`;
      if (err.message.includes('Session expired or not found')) {
        setError(err.message);
      } else {
        setError(errorMessage);
      }
      throw err;
    } finally {
      if (!suppressLoadingIndicator) {
        setIsLoading(false);
      }
    }
  }, [setError]);

  const handleOutcome = useCallback((finalCards) => {
    const victoryCard = finalCards.find(card => card.Category === 'victory');
    const defeatCard = finalCards.find(card => card.Category === 'defeat');

    clearHypothesisLocalStorage(); 

    let chosenOutcomeCard = null;
    if (victoryCard) {
      chosenOutcomeCard = victoryCard;
      console.log('Game ended with Victory. Outcome card data set.');
    } else if (defeatCard) {
      chosenOutcomeCard = defeatCard;
      console.log('Game ended with Defeat. Outcome card data set.');
    } else {
      console.warn('No specific outcome card (victory or defeat) found. Using default defeat.');
      chosenOutcomeCard = { 
        Code: 'DD',
        Type: 'story',
        Category: 'defeat', 
        Title: translations[lang]?.game_over_main_text,
        Subtitle: translations[lang]?.game_over_message,
        FieldValue: translations[lang]?.no_outcome_card_found
      };
    }
    setOutcomeCardData(chosenOutcomeCard);
    localStorage.setItem('outcomeCardData', JSON.stringify(chosenOutcomeCard));
  }, [setOutcomeCardData, lang, translations, clearHypothesisLocalStorage]); 

  const navigateToCardInCarousel = useCallback((cardId) => {
    if (window.location.hash.substring(1) === 'outcome') { 
      console.warn('navigateToCardInCarousel: Not navigating as current hash is "outcome".');
      return;
    }
    const cardIndex = currentCards.findIndex(card => card.CardId === cardId);
    if (cardIndex !== -1) {
      const event = new CustomEvent('navigateToCarouselItem', { detail: { index: cardIndex } });
      window.dispatchEvent(event);
      console.log(`Navigated to card with ID ${cardId} in carousel.`);
    } else {
      console.warn(`Card with ID ${cardId} not found in currentCards for carousel navigation.`);
    }
  }, [currentCards]);

  const checkSession = useCallback(async (sId) => {
    if (!sId) {
      return false;
    }
    try {
      const res = await handleLocalApiCall('GET', `/game/session/check/${sId}`);
      return Boolean(res && res.Status);
    } catch (err) {
      console.error('Error checking session:', err);
      return false;
    }
  }, []);

  const fetchSessionStatus = useCallback(async (forceRefresh = false, suppressLoadingIndicator = false, isPollingCall = false) => {
    let currentSessionId = sessionId || localStorage.getItem('sessionId');
    let currentCardLang = cardLang || localStorage.getItem('cardLanguage');

    if (window.location.hash.substring(1) === 'outcome' && !forceRefresh) {
      console.log('fetchSessionStatus: Current hash is outcome and not forced refresh. Skipping status fetch.');
      return; 
    }

    if (!currentSessionId) {
      setError(translations[lang]?.error_no_active_session);
      setGameStatus('initial'); 
      window.location.hash = 'initial';
      return;
    }
    if (!currentCardLang) {
      currentCardLang = 'EN';
      console.warn('Card language not found, defaulting to EN for session status fetch.');
    }

    try {
      setIsPollingUpdate(isPollingCall);

      const response = await makeApiCall('POST', '/game/session/read', { 
        sessionId: currentSessionId, 
        language: currentCardLang 
      }, suppressLoadingIndicator);

      setSessionCode(response.Code); 
      setCurrentCards(response.Cards);
      setCurrentCounter(response.Counter);
      console.log('Session status updated:', response);

      if (response.Status === 'mission_ready') {
        setGameStatus('mission');
        window.location.hash = 'mission';
      } else if (response.Status === 'started') {
        setGameStatus('game');
        window.location.hash = 'game';
      } else if (response.Status === 'gameover' || response.Status === 'victory') { 
        handleOutcome(response.Cards);
        setGameStatus('outcome');
        window.location.hash = 'outcome';
      } else {
        console.warn('Unexpected session status during refresh:', response.Status);
        setGameStatus('initial'); 
        window.location.hash = 'initial';
      }

    } catch (err) {
      console.error('Error fetching session status:', err);
      setError(err.message || translations[lang]?.unknown_error_occurred);
      
      setSessionId(null);
      setSessionCode(null); 
      localStorage.removeItem('sessionId');
      localStorage.removeItem('cardLanguage');
      clearHypothesisLocalStorage();
      localStorage.removeItem('outcomeCardData'); 
      setGameStatus('initial'); 
      window.location.hash = 'initial';
    }
  }, [sessionId, cardLang, makeApiCall, setCurrentCards, setCurrentCounter, setError, translations, lang, clearHypothesisLocalStorage, handleOutcome, setGameStatus, gameStatus]); 


  const fetchMissionCard = useCallback(async (sId, currentCardLang) => {
    if (!sId) {
      setError(translations[lang]?.error_no_active_session);
      setGameStatus('initial'); 
      window.location.hash = 'initial'; 
      return;
    }
    const languageForCards = currentCardLang || cardLang; 
    try {
      const response = await makeApiCall('GET', `/session/status/${sId}?l=${languageForCards}`); 
      setSessionCode(response.Code); 
      setCurrentCards(response.Cards); 
      setCurrentCounter(response.Counter); 
      setGameStatus('mission'); 
      window.location.hash = 'mission'; 
    } catch (err) {
      console.error('Error loading mission card:', err);
      setError(err.message);
      setSessionId(null);
      setSessionCode(null); 
      localStorage.removeItem('sessionId');
      clearHypothesisLocalStorage();
      localStorage.removeItem('outcomeCardData'); 
      setGameStatus('initial'); 
      window.location.hash = 'initial'; 
    }
  }, [makeApiCall, lang, translations, setError, setCurrentCards, setCurrentCounter, setSessionId, cardLang, clearHypothesisLocalStorage, setGameStatus]); 

  const createSession = useCallback(async (code, playerUILang, cardDataLang) => {
    try {
      const oldSessionId = localStorage.getItem('sessionId');
      const response = await makeApiCall('POST', '/game/session/create', { code, language: cardDataLang });

      if (oldSessionId && oldSessionId !== response.SessionId) {
        clearHypothesisLocalStorage();
      }
      setSessionId(response.SessionId); 
      setSessionCode(response.Code); 
      localStorage.setItem('sessionId', response.SessionId); 
      console.log('Session created:', response.SessionId);
      setCurrentCards(response.Cards); 
      setCurrentCounter(response.Counter); 
      setGameStatus('mission'); 
      localStorage.removeItem('outcomeCardData'); 
      window.location.hash = 'mission'; 

    }
     catch (err) {
      console.error('Error creating session:', err);
      setError(err.message); 
      setSessionId(null);
      setSessionCode(null); 
      localStorage.removeItem('sessionId');
      clearHypothesisLocalStorage();
      localStorage.removeItem('outcomeCardData'); 
      setGameStatus('initial'); 
      window.location.hash = 'initial'; 
    }
  }, [makeApiCall, clearHypothesisLocalStorage, setSessionId, setError, setCurrentCards, setCurrentCounter, lang, translations, setGameStatus]); 

  const joinSession = useCallback(async (id, playerUILang, cardDataLang) => {
    const oldSessionId = localStorage.getItem('sessionId');
    if (oldSessionId && oldSessionId !== id) {
      clearHypothesisLocalStorage();
    }
    setSessionId(id); 
    localStorage.setItem('sessionId', id); 

    try {
      const response = await makeApiCall('POST', '/game/session/join', { sessionId: id, language: cardDataLang });

      setSessionId(response.SessionId);
      setSessionCode(response.Code); 
      localStorage.setItem('sessionId', response.SessionId);
      setCurrentCards(response.Cards);
      setCurrentCounter(response.Counter);

      console.log('Successfully joined session:', response.SessionId);

      if (response.Status === 'mission_ready') {
        setGameStatus('mission');
        localStorage.removeItem('outcomeCardData'); 
        window.location.hash = 'mission';
      } else if (response.Status === 'started') {
        setGameStatus('game');
        localStorage.removeItem('outcomeCardData'); 
        window.location.hash = 'game';
      } else if (response.Status === 'gameover' || response.Status === 'victory') { 
        handleOutcome(response.Cards); 
        setGameStatus('outcome');
        window.location.hash = 'outcome';
      } else {
        console.warn('Unexpected session status during join:', response.Status);
        setGameStatus('initial'); 
        localStorage.removeItem('outcomeCardData'); 
        window.location.hash = 'initial';
      }

    } catch (err) {
      console.error('Error joining session:', err);
      setError(err.message); 
      setSessionId(null);
      setSessionCode(null); 
      localStorage.removeItem('sessionId');
      clearHypothesisLocalStorage();
      localStorage.removeItem('outcomeCardData'); 
      setGameStatus('initial'); 
      window.location.hash = 'initial'; 
    }
  }, [makeApiCall, clearHypothesisLocalStorage, setSessionId, setCurrentCards, setCurrentCounter, setError, handleOutcome, lang, translations, setGameStatus]); 

  const playCard = useCallback(async (sId, cardIdToPlay, langForCards) => {
    if (!sId || !cardIdToPlay || !langForCards) {
      setError(translations[lang]?.missing_play_card_params_error);
      return;
    }
    try {
      setIsLoading(true);
      const response = await makeApiCall('POST', '/game/card/play', { 
        sessionId: sId,
        cardId: cardIdToPlay,
        language: langForCards 
      });

      setCurrentCounter(response.Counter);
      setCurrentCards(response.Cards);

      if (response.Status === 'gameover' || response.Status === 'victory') {
        console.log(`Game ended with status: ${response.Status}. Preparing outcome data and navigating.`);
        handleOutcome(response.Cards); 
        setGameStatus('outcome');
        window.location.hash = 'outcome';
      } else if (response.Status === 'started') {
        setGameStatus('game');
        localStorage.removeItem('outcomeCardData'); 
        window.location.hash = 'game'; 
      } else {
        console.warn('Unexpected status after playing card:', response.Status);
        localStorage.removeItem('outcomeCardData');
      }
    } catch (err) {
      console.error('Error playing card:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [makeApiCall, setCurrentCounter, setCurrentCards, setError, handleOutcome, translations, lang, setGameStatus]); 


  const startSession = useCallback(async () => {
    if (!sessionId) {
      setError(translations[lang]?.error_no_active_session);
      setGameStatus('initial'); 
      window.location.hash = 'initial'; 
      return;
    }
    
    const missionCard = currentCards.find(card => card.Type && card.Type.toLowerCase() === "mission"); 
    if (!missionCard) {
      setError(translations[lang]?.error_loading_mission_card);
      return;
    }

    try {
        await playCard(sessionId, missionCard.CardId, cardLang);
    } catch (err) {
        console.error('Error starting session via playCard:', err);
        setError(err.message);
        setGameStatus('initial'); 
        window.location.hash = 'initial';
    }
  }, [sessionId, currentCards, cardLang, playCard, translations, lang, setError, setGameStatus]); 


  const handleQrCodeScanned = useCallback(async (qrCodeData) => {
    setIsPollingUpdate(false);
    
    if (!sessionId) {
      setError(translations[lang]?.error_no_active_session);
      setGameStatus('initial'); 
      window.location.hash = 'initial'; 
      return;
    }

    if (qrScannerModalRef.current && window.bootstrap) {
      const bsModal = window.bootstrap.Modal.getInstance(qrScannerModalRef.current);
      if (bsModal) bsModal.hide();
    }

    const missionUrlPrefix = 'https://evapp.pixelings.com/?c=';
    if (qrCodeData.startsWith(missionUrlPrefix)) {
      try {
        const url = new URL(qrCodeData);
        const codeFromUrl = url.searchParams.get('c');

        if (codeFromUrl && sessionCode && codeFromUrl === sessionCode) {
          const missionCard = currentCards.find(card => card.Type && card.Type.toLowerCase() === "mission");
          if (missionCard) {
            console.log(`Mission card URL scanned. Code '${codeFromUrl}' matches current session code. Navigating to mission card.`);
            navigateToCardInCarousel(missionCard.CardId);
          } 
        } else if (codeFromUrl && sessionCode && codeFromUrl !== sessionCode) {
          console.warn(`Mission card URL scanned. Code '${codeFromUrl}' does not match current session code '${sessionCode}'.`);
          setError(translations[lang]?.mission_card_code_mismatch_error);
        } else {
          console.warn(`Invalid or incomplete mission card URL scanned: ${qrCodeData}`);
          setError(translations[lang]?.invalid_unrecognized_qr_code_error);
        }
      } catch (e) {
        console.error('Error parsing mission card URL:', e);
        setError(translations[lang]?.invalid_unrecognized_qr_code_error);
      }
      setLastScannedCardCode(null); 
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const appSchemaPrefix = 'evapp://';
    if (qrCodeData.startsWith(appSchemaPrefix)) {
      const cardIdFromQr = qrCodeData.substring(appSchemaPrefix.length);
      
      const cardExistsInCarousel = currentCards.some(card => card.CardId === cardIdFromQr);

      if (cardExistsInCarousel) {
        console.log(`Card '${cardIdFromQr}' already exists in carousel. Navigating to it.`);
        navigateToCardInCarousel(cardIdFromQr);
        setLastScannedCardCode(null); 
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      } else {
        console.log(`Card '${cardIdFromQr}' not in carousel. Attempting to play it.`);
        try {
          setLastScannedCardCode(cardIdFromQr); 
          await playCard(sessionId, cardIdFromQr, cardLang); 
        } catch (err) {
          console.error(`Error playing scanned card '${cardIdFromQr}':`, err);
          setError(err.message);
        } finally {
          setLastScannedCardCode(null);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    console.warn(`Unrecognized QR code format: ${qrCodeData}`);
    setError(translations[lang]?.invalid_unrecognized_qr_code_error);
    setLastScannedCardCode(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [sessionId, sessionCode, currentCards, cardLang, translations, lang, playCard, setError, showToastMessage, navigateToCardInCarousel, setLastScannedCardCode, qrScannerModalRef, setGameStatus]); 


  const validateCodeAndFetchLanguages = useCallback(async (code) => {
    setIsLoading(true);
    setError(null); 
    try {
      const response = await makeApiCall('GET', `/game/validate/${code}`);
      const fetchedAvailableLanguages = response || [];
      setAvailableLanguages(fetchedAvailableLanguages);

      let effectiveCardLanguage = 'EN'; 

      if (fetchedAvailableLanguages.includes(lang)) {
        effectiveCardLanguage = lang;
        console.log(`Using player's UI language '${lang}' for card data.`);
      }
      else if (fetchedAvailableLanguages.includes('EN')) {
        effectiveCardLanguage = 'EN';
        console.log(`Player's UI language '${lang}' not available. Falling back to 'EN' for card data.`);
      }
      else if (fetchedAvailableLanguages.length > 0) {
        effectiveCardLanguage = fetchedAvailableLanguages[0];
        console.log(`Player's UI language and 'EN' not available. Falling back to '${effectiveCardLanguage}' for card data.`);
      } else {
        console.warn(`No card data languages found for code: ${code}.`);
        setError(translations[lang]?.error_code_not_found);
        setCardLang('EN'); 
        setGameStatus('initial'); 
        localStorage.removeItem('outcomeCardData'); 
        return false;
      }

      setCardLang(effectiveCardLanguage); 
      localStorage.setItem('cardLanguage', effectiveCardLanguage); 

      return true; 
    } catch (err) {
      console.error('Error validating code and fetching languages:', err);
      setAvailableLanguages([]); 
      setCardLang('EN'); 
      localStorage.removeItem('cardLanguage');
      localStorage.removeItem('outcomeCardData'); 
      setGameStatus('initial'); 
      return false; 
    } finally {
      setIsLoading(false);
    }
  }, [makeApiCall, setError, setIsLoading, lang, translations, setGameStatus]);

  const returnToSession = useCallback(async () => {
    const storedSessionId = localStorage.getItem('sessionId');
    const storedCardLang = localStorage.getItem('cardLanguage');

    if (storedSessionId && storedCardLang) {
      setSessionId(storedSessionId); 
      localStorage.setItem('sessionId', storedSessionId); 
      console.log('Returning to saved session:', storedSessionId);
      const success = await joinSession(storedSessionId, lang, storedCardLang);
      if (!success) {
        setSessionId(null);
        setSessionCode(null); 
        localStorage.removeItem('sessionId');
        localStorage.removeItem('cardLanguage');
        clearHypothesisLocalStorage();
        localStorage.removeItem('outcomeCardData'); 
        setGameStatus('initial'); 
        window.location.hash = 'initial'; 
      }
    } else {
      setError(translations[lang]?.error_no_active_session);
      localStorage.removeItem('sessionId');
      localStorage.removeItem('cardLanguage');
      clearHypothesisLocalStorage();
      localStorage.removeItem('outcomeCardData'); 
      setGameStatus('initial'); 
      window.location.hash = 'initial'; 
    }
  }, [lang, translations, setSessionId, setError, clearHypothesisLocalStorage, joinSession, setGameStatus]);

  const getCardImagePath = useCallback((card) => {
    if (!card) {
        console.warn('getCardImagePath called with null or undefined card.');
        return '';
    }

    if (card.Image && card.Image.trim() !== '') {
        return `/img/${card.Image}`;
    }

    if (card.Category) {
        let imageName = card.Category.toLowerCase();
        if (card.SubCategory && card.SubCategory.trim() !== '') {
            imageName = `${imageName}-${card.SubCategory.toLowerCase()}`;
        }
        return `/assets/cards/${imageName}.png`;
    }
    
    return '';
  }, []);

  const formatFieldValue = useCallback((fieldValue) => {
    if (!fieldValue) return '';
    const regex = /\B\[\$([LPE])(\d+)\]\B/g;
    return fieldValue.replace(regex, (match, type, numStr) => {
      const num = parseInt(numStr, 10);
      let badgeClass = '';
      switch (type) {
          case 'L': badgeClass = 'bg-success'; break;
          case 'P': badgeClass = 'bg-danger'; break;
          case 'E': badgeClass = 'bg-info'; break;
          default: return match;
      }
      return `<span class="badge ${badgeClass}">${num}</span>`;
    });
  }, []);

  const handleHypothesisChange = useCallback((cardId, isChecked) => { 
    localStorage.setItem(`hypothesis_${cardId}`, isChecked); 
    console.log(`Hypothesis for card ${cardId} saved as: ${isChecked}`);
    setHypothesisChangedFlag(prev => prev + 1); 
  }, []);

  const showAlertModal = useCallback((title, message, isConfirmation, onConfirm, onClose, confirmButtonText, cancelButtonText) => {
    const wrappedOnConfirm = () => {
      if (alertModalRef.current) {
        const bsModalInstance = window.bootstrap.Modal.getInstance(alertModalRef.current);
        if (bsModalInstance) {
          bsModalInstance.hide();
        }
      }
      if (onConfirm) {
        onConfirm();
      }
    };

    const wrappedOnClose = () => {
      if (alertModalRef.current) {
        const bsModalInstance = window.bootstrap.Modal.getInstance(alertModalRef.current);
        if (bsModalInstance) {
          bsModalInstance.hide();
        }
      }
      if (onClose) {
        onClose();
      }
    };

    setAlertModalState({
      title,
      message,
      isConfirmation,
      onConfirm: wrappedOnConfirm,
      onClose: wrappedOnClose,
      confirmButtonText,
      cancelButtonText
    });
    alertModalRef.current?.show();
  }, [alertModalRef]);


  const verifySolution = useCallback(async (submittedCardCodes) => {
    if (!sessionId) {
      showAlertModal(
        translations[lang]?.error_title,
        translations[lang]?.no_session_id_error,
        false, null, () => window.location.hash = 'initial' 
      );
      return;
    }

    try {
      setIsLoading(true); 
      const data = await makeApiCall('POST', `/game/guess`, { 
        sessionId: sessionId, 
        language: cardLang, 
        cards: submittedCardCodes 
      });

      setCurrentCounter(data.Counter);
      setCurrentCards(data.Cards);

      if (data.Status === 'victory' || data.Status === 'gameover') { 
        console.log(`Game ended with status: ${data.Status}. Preparing outcome data and navigating.`);
        handleOutcome(data.Cards);
        setGameStatus('outcome');
        window.location.hash = 'outcome';
      } else {
        showAlertModal(
          translations[lang]?.incorrect_solution_title, 
          translations[lang]?.try_again_message,
          false, 
          null,  
          null   
        );
      }
    } catch (err) {
      console.error('Error verifying solution:', err);
      showAlertModal(
        translations[lang]?.error_title,
        err.message || translations[lang]?.unknown_error_occurred,
        false, null, null
      );
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, cardLang, translations, makeApiCall, handleOutcome, showAlertModal, setIsLoading, setCurrentCounter, setCurrentCards, lang, setGameStatus]); 


  const contextValue = {
    sessionId, setSessionId, 
    sessionCode, 
    currentCounter, setCurrentCounter, currentCards, setCurrentCards,
    isLoading, error, setError, lang, setLang, translations, isLocalhost,
    makeApiCall, clearHypothesisLocalStorage, createSession, joinSession,
    fetchMissionCard, startSession, handleQrCodeScanned, playCard, 
    handleOutcome, returnToSession, getCardImagePath, formatFieldValue,
    navigateToCardInCarousel, handleHypothesisChange,
    qrScannerModalRef,
    outcomeCardData, 
    showToast,
    toastMessage,
    showToastMessage,
    hypothesisChangedFlag, 
    lastScannedCardCode, setLastScannedCardCode, 
    alertModalRef,       
    alertModalState,      
    validateCodeAndFetchLanguages, 
    availableLanguages,   
    showAlertModal,       
    verifySolution,       
    resetGame,            
    cardLang, 
    checkSession,
    fetchSessionStatus,
    gameStatus,
    isPollingUpdate,setIsPollingUpdate
  };

  return (
    <GameContext.Provider value={contextValue}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};
